import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';

export const COMPOSE_STRAIGHT = 0;
export const COMPOSE_CHECKER = 1;
export const COMPOSE_SOLID = 2;
export type ComposeMode =
  | typeof COMPOSE_STRAIGHT
  | typeof COMPOSE_CHECKER
  | typeof COMPOSE_SOLID;

export interface ComposeOptions {
  mode?: ComposeMode;
  background?: readonly [number, number, number];
  /** Checkerboard square size in pixels. */
  checkerSize?: number;
  checkerLight?: number;
  checkerDark?: number;
  /** Set when the source carries premultiplied colour. */
  premultiplied?: boolean;
}

/**
 * Flattens the working image into `rgba8unorm`.
 *
 * `COMPOSE_STRAIGHT` is the export path (alpha preserved). The solid mode exists
 * for garment preview: a fringe that is invisible against white is exactly what
 * gets a POD listing rejected once it is printed on a black tee.
 */
export function compose(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  options: ComposeOptions = {},
): void {
  if (dst.format !== 'rgba8unorm') {
    throw new Error(`[gpu-core] compose writes rgba8unorm, got ${dst.format}`);
  }
  const bg = options.background ?? [1, 1, 1];
  const params = new Struct(48)
    .i(0, dst.width, dst.height)
    .u(8, options.mode ?? COMPOSE_STRAIGHT, options.premultiplied ? 1 : 0)
    .f(16, bg[0], bg[1], bg[2], 1)
    .f(32, options.checkerSize ?? 12, options.checkerLight ?? 0.93, options.checkerDark ?? 0.82, 0);

  ctx.dispatch(
    {
      module: 'compose.wgsl',
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'storageTexture', format: 'rgba8unorm' },
        { kind: 'uniform' },
      ],
    },
    [src, dst, ctx.uniform(params.bytes)],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'compose',
  );
}
